import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  AlertTitle,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Security,
  VpnKey,
  History,
  Business,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Refresh,
  CheckCircle,
  Warning,
  QrCode2,
  Key,
} from '@mui/icons-material';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentCompany, selectCurrentUser } from '@/store/slices/authSlice';
import { api } from '@/services/api';
import toast from 'react-hot-toast';

interface TwoFactorStatus {
  enabled: boolean;
  hasBackupCodes: boolean;
  backupCodesCount: number;
}

interface Session {
  id: string;
  device: string;
  browser: string;
  os: string;
  ip: string;
  location: string;
  lastActivity: string;
  current: boolean;
}

const SecurityPage: React.FC = () => {
  const theme = useTheme();
  const company = useAppSelector(selectCurrentCompany);
  const user = useAppSelector(selectCurrentUser);

  // States
  const [loading, setLoading] = useState(true);
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);

  // Dialog states
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [disable2FAOpen, setDisable2FAOpen] = useState(false);
  const [backupCodesOpen, setBackupCodesOpen] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState<string[]>([]);

  // Form states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [totpCode, setTotpCode] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Load data
  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    setLoading(true);
    try {
      // Load 2FA status
      const twoFaResponse = await api.get('/auth/2fa/status');
      if (twoFaResponse?.success) {
        setTwoFactorStatus(twoFaResponse.data);
      }

      // Load sessions (mock for now - would need backend endpoint)
      setSessions([
        {
          id: '1',
          device: 'Desktop',
          browser: 'Chrome',
          os: 'Windows',
          ip: '192.168.1.1',
          location: 'São Paulo, BR',
          lastActivity: new Date().toISOString(),
          current: true,
        },
      ]);
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Change Password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('A nova senha deve ter pelo menos 8 caracteres');
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.put('/auth/change-password', {
        currentPassword,
        newPassword,
      });

      if (response?.success) {
        toast.success('Senha alterada com sucesso!');
        setChangePasswordOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(response?.error?.message || 'Erro ao alterar senha');
      }
    } catch (error: any) {
      toast.error(error?.error?.message || 'Erro ao alterar senha');
    } finally {
      setFormLoading(false);
    }
  };

  // Disable 2FA
  const handleDisable2FA = async () => {
    if (!totpCode || !currentPassword) {
      toast.error('Preencha todos os campos');
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.post('/auth/2fa/disable', {
        code: totpCode,
        password: currentPassword,
      });

      if (response?.success) {
        toast.success('2FA desabilitado com sucesso');
        setDisable2FAOpen(false);
        setTotpCode('');
        setCurrentPassword('');
        loadSecurityData();
      } else {
        toast.error(response?.error?.message || 'Erro ao desabilitar 2FA');
      }
    } catch (error: any) {
      toast.error(error?.error?.message || 'Erro ao desabilitar 2FA');
    } finally {
      setFormLoading(false);
    }
  };

  // Regenerate Backup Codes
  const handleRegenerateBackupCodes = async () => {
    if (!totpCode) {
      toast.error('Digite o código TOTP');
      return;
    }

    setFormLoading(true);
    try {
      const response = await api.post('/auth/2fa/regenerate-backup-codes', {
        code: totpCode,
      });

      if (response?.success) {
        setNewBackupCodes(response.data.backupCodes);
        setBackupCodesOpen(true);
        setTotpCode('');
        toast.success('Códigos de backup regenerados!');
        loadSecurityData();
      } else {
        toast.error(response?.error?.message || 'Erro ao regenerar códigos');
      }
    } catch (error: any) {
      toast.error(error?.error?.message || 'Erro ao regenerar códigos');
    } finally {
      setFormLoading(false);
    }
  };

  // Copy backup codes
  const copyAllBackupCodes = () => {
    navigator.clipboard.writeText(newBackupCodes.join('\n'));
    toast.success('Códigos copiados!');
  };

  // End session
  const handleEndSession = async (sessionId: string) => {
    try {
      // Would call backend to end session
      toast.success('Sessão encerrada');
      setSessions(sessions.filter((s) => s.id !== sessionId));
    } catch (error) {
      toast.error('Erro ao encerrar sessão');
    }
  };

  // End all other sessions
  const handleEndAllSessions = async () => {
    try {
      // Would call backend to end all other sessions
      toast.success('Todas as outras sessões foram encerradas');
      setSessions(sessions.filter((s) => s.current));
    } catch (error) {
      toast.error('Erro ao encerrar sessões');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
        Segurança
      </Typography>

      {/* Banner do Usuário */}
      <Alert
        severity="info"
        icon={<Business />}
        sx={{
          mb: 3,
          backgroundColor: alpha(theme.palette.info.main, 0.08),
          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          '& .MuiAlert-icon': { color: theme.palette.info.main },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600 }}>
          Configurações de segurança para: {user?.name || user?.email}
        </AlertTitle>
        <Typography variant="body2" color="text.secondary">
          Empresa ativa: {company?.tradingName || company?.name || 'Não identificada'}
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Autenticação 2FA */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <QrCode2 sx={{ mr: 1, fontSize: 20 }} />
              Autenticação em Dois Fatores (2FA)
            </Typography>

            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 1,
                backgroundColor: twoFactorStatus?.enabled
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.warning.main, 0.1),
                border: `1px solid ${
                  twoFactorStatus?.enabled
                    ? alpha(theme.palette.success.main, 0.3)
                    : alpha(theme.palette.warning.main, 0.3)
                }`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {twoFactorStatus?.enabled ? (
                  <CheckCircle color="success" />
                ) : (
                  <Warning color="warning" />
                )}
                <Typography fontWeight="bold">
                  {twoFactorStatus?.enabled ? '2FA Habilitado' : '2FA Não Configurado'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {twoFactorStatus?.enabled
                  ? 'Sua conta está protegida com autenticação em dois fatores.'
                  : 'Configure o 2FA para adicionar uma camada extra de segurança. O 2FA será configurado no próximo login.'}
              </Typography>
            </Box>

            {twoFactorStatus?.enabled && (
              <>
                {/* Backup Codes Status */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <Key sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    Códigos de backup restantes:{' '}
                    <Chip
                      label={twoFactorStatus.backupCodesCount}
                      size="small"
                      color={twoFactorStatus.backupCodesCount > 3 ? 'success' : 'warning'}
                    />
                  </Typography>
                </Box>

                {/* Regenerate Backup Codes */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Regenerar Códigos de Backup
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Código TOTP"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputProps={{ maxLength: 6 }}
                      sx={{ width: 120 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Refresh />}
                      onClick={handleRegenerateBackupCodes}
                      disabled={totpCode.length !== 6 || formLoading}
                    >
                      Regenerar
                    </Button>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Disable 2FA Button */}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDisable2FAOpen(true)}
                >
                  Desabilitar 2FA
                </Button>
              </>
            )}

            {!twoFactorStatus?.enabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Faça logout e login novamente para configurar o 2FA.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Senha */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <VpnKey sx={{ mr: 1, fontSize: 20 }} />
              Senha
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Altere sua senha regularmente para manter sua conta segura. Use uma senha forte com
              letras maiúsculas, minúsculas, números e caracteres especiais.
            </Typography>

            <Button
              variant="outlined"
              color="warning"
              onClick={() => setChangePasswordOpen(true)}
            >
              Alterar Senha
            </Button>
          </Paper>
        </Grid>

        {/* Sessões Ativas */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <History sx={{ mr: 1, fontSize: 20 }} />
              Sessões Ativas
            </Typography>

            <List>
              {sessions.map((session) => (
                <ListItem key={session.id} divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {session.browser} - {session.os}
                        {session.current && <Chip label="Sessão Atual" color="success" size="small" />}
                      </Box>
                    }
                    secondary={
                      <>
                        IP: {session.ip} • {session.location} •{' '}
                        {new Date(session.lastActivity).toLocaleString('pt-BR')}
                      </>
                    }
                  />
                  {!session.current && (
                    <ListItemSecondaryAction>
                      <Button size="small" color="error" onClick={() => handleEndSession(session.id)}>
                        Encerrar
                      </Button>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>

            {sessions.length > 1 && (
              <Button
                variant="outlined"
                color="error"
                fullWidth
                sx={{ mt: 2 }}
                onClick={handleEndAllSessions}
              >
                Encerrar Todas as Outras Sessões
              </Button>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog: Alterar Senha */}
      <Dialog open={changePasswordOpen} onClose={() => !formLoading && setChangePasswordOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Alterar Senha</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Senha Atual"
              type={showPasswords.current ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      edge="end"
                    >
                      {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Nova Senha"
              type={showPasswords.new ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              helperText="Mínimo 8 caracteres"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      edge="end"
                    >
                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirmar Nova Senha"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              error={confirmPassword !== '' && confirmPassword !== newPassword}
              helperText={confirmPassword !== '' && confirmPassword !== newPassword ? 'As senhas não coincidem' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      edge="end"
                    >
                      {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordOpen(false)} disabled={formLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={!currentPassword || !newPassword || newPassword !== confirmPassword || formLoading}
            startIcon={formLoading && <CircularProgress size={16} />}
          >
            {formLoading ? 'Alterando...' : 'Alterar Senha'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Desabilitar 2FA */}
      <Dialog open={disable2FAOpen} onClose={() => !formLoading && setDisable2FAOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Desabilitar 2FA</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Atenção</AlertTitle>
            Desabilitar o 2FA reduzirá a segurança da sua conta. Você precisará configurar novamente no próximo login.
          </Alert>
          <TextField
            fullWidth
            label="Senha Atual"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Código TOTP"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            margin="normal"
            placeholder="000000"
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisable2FAOpen(false)} disabled={formLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDisable2FA}
            variant="contained"
            color="error"
            disabled={!currentPassword || totpCode.length !== 6 || formLoading}
            startIcon={formLoading && <CircularProgress size={16} />}
          >
            {formLoading ? 'Desabilitando...' : 'Desabilitar 2FA'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Backup Codes */}
      <Dialog open={backupCodesOpen} onClose={() => setBackupCodesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novos Códigos de Backup</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Guarde estes códigos em local seguro!</AlertTitle>
            Os códigos anteriores foram invalidados. Cada código só pode ser usado uma vez.
          </Alert>

          <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Códigos de Backup
              </Typography>
              <Button size="small" startIcon={<ContentCopy />} onClick={copyAllBackupCodes}>
                Copiar Todos
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
                fontFamily: 'monospace',
              }}
            >
              {newBackupCodes.map((code, index) => (
                <Chip
                  key={index}
                  label={code}
                  variant="outlined"
                  size="small"
                  sx={{ fontFamily: 'monospace', justifyContent: 'flex-start' }}
                />
              ))}
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupCodesOpen(false)} variant="contained">
            Entendi, Salvei os Códigos
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityPage;
